import { Button } from "@/components/ui/button";
import { Calendar, ArrowLeft, ChevronDown, Globe, Trophy, Users, Loader2, Share2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TournamentCard from "./TournamentCard";
import LeagueCard from "./LeagueCard";
import ChatIcon from "./ChatIcon";
import ChatScreen from "./ChatScreen";
import { useOrganizerProfile, OrganizerEvent } from "@/hooks/useOrganizerProfile";

import OrganizerRankingTab from "./organizer/OrganizerRankingTab";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOrganizerFollow } from "@/hooks/useOrganizerFollow";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

interface OrganizerProfileProps {
  organizerId: string;
  onBack: () => void;
  onEventClick: (event: any) => void;
  onChatClick?: () => void;
  onChatCountChange?: (count: number) => void;
}

const OrganizerProfile = ({ organizerId, onBack, onEventClick, onChatClick, onChatCountChange }: OrganizerProfileProps) => {
  const { profile, loading, error } = useOrganizerProfile(organizerId);
  const { t } = useLanguage();
  const { user } = useAuth();
  const { isFollowing, followOrganizer, unfollowOrganizer } = useOrganizerFollow(organizerId);
  const [showChat, setShowChat] = useState(false);
  const [chatCount, setChatCount] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [localFollowersCount, setLocalFollowersCount] = useState(0);

  useEffect(() => {
    if (profile) setLocalFollowersCount(profile.followersCount);
  }, [profile?.followersCount]);

  const handleFollow = async () => {
    setLocalFollowersCount(prev => prev + 1);
    await followOrganizer();
  };
  const handleUnfollow = async () => {
    setLocalFollowersCount(prev => Math.max(0, prev - 1));
    await unfollowOrganizer();
  };

  const getInitials = (name: string) =>
    name.split(' ').map(w => w.charAt(0)).join('').toUpperCase().slice(0, 2);

  const handleChatClick = () => {
    setShowChat(true);
    onChatClick?.();
  };

  const handleChatBack = () => {
    setShowChat(false);
    const newCount = chatCount + 1;
    setChatCount(newCount);
    onChatCountChange?.(newCount);
  };

  const handleShareProfile = async () => {
    const url = `${window.location.origin}/?organizer=${organizerId}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: profile?.name, url });
      } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success(t('organizerProfile.profileCopied'));
    }
  };

  const handleEventClick = (event: OrganizerEvent) => {
    if (event.eventType === 'league') {
      onEventClick({
        id: event.id, title: event.title, sport: event.sport, date: event.date,
        endDate: event.endDate, time: event.time, start_time: event.start_time,
        end_time: event.end_time, game_days: event.game_days, location: event.location,
        participants: event.participants, maxParticipants: event.maxParticipants,
        registrationFee: event.registrationFee, start_date: event.date,
        end_date: event.endDate, entry_fee: event.registrationFee,
        max_participants: event.maxParticipants, event_type: 'league', isLeague: true,
        status: event.status, imageUrl: event.imageUrl, image_url: event.imageUrl,
        matchType: event.matchType, leagueFormat: event.leagueFormat,
        league_format: event.league_format, genderCategories: event.genderCategories,
        subcategories: event.subcategories, registrationDeadline: event.registrationDeadline,
        registration_deadline: event.registrationDeadline, total_weeks: event.total_weeks,
        matches_per_week: event.matches_per_week, address: event.address,
        postal_code: event.postal_code, organizer_id: event.organizer_id,
        organizer: profile?.name, rules: event.rules, description: event.description,
        categories: event.category ? [event.category] : [],
      });
    } else {
      onEventClick({
        id: event.id, title: event.title, sport: event.sport, date: event.date,
        endDate: event.endDate, time: event.time, start_time: event.start_time,
        end_time: event.end_time, game_days: event.game_days, location: event.location,
        participants: event.participants, maxParticipants: event.maxParticipants,
        registrationFee: event.registrationFee,
        start_date: event.date, end_date: event.endDate, entry_fee: event.registrationFee,
        max_participants: event.maxParticipants, event_type: event.eventType,
        status: event.status, image_url: event.imageUrl, imageUrl: event.imageUrl,
        matchType: event.matchType,
        genderCategories: event.genderCategories,
        subcategories: event.subcategories,
        categories: event.genderCategories && event.genderCategories.length > 0
          ? event.genderCategories
          : (event.category ? [event.category] : []),
        registrationDeadline: event.registrationDeadline,
        registration_deadline: event.registrationDeadline,
        address: event.address, postal_code: event.postal_code,
        organizer_id: event.organizer_id, organizer: profile?.name,
        rules: event.rules, description: event.description,
        tournament_format: (event as any).tournament_format,
        tournamentFormat: (event as any).tournament_format,
      });
    }
  };

  if (showChat && profile) {
    return (
      <ChatScreen chatId={profile.id} partnerName={profile.name}
        partnerAvatar={profile.avatar} onBack={handleChatBack} />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('organizerProfile.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="space-y-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="p-2">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-foreground">{t('organizerProfile.title')}</h1>
        </div>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">{error || t('organizerProfile.notFound')}</p>
            <Button onClick={onBack} className="mt-4">{t('organizerProfile.back')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderEventList = (events: OrganizerEvent[], type: 'tournaments' | 'leagues') => {
    if (events.length === 0) return null;
    return (
      <div className="space-y-3">
        {events.map(event =>
          type === 'leagues' ? (
            <LeagueCard key={event.id} league={{
              id: event.id, title: event.title, organizer: profile.name, sport: event.sport,
              date: event.date, endDate: event.endDate, location: event.location,
              registrationFee: event.registrationFee, imageUrl: event.imageUrl,
              matchType: event.matchType, leagueFormat: event.leagueFormat,
              genderCategories: event.genderCategories, subcategories: event.subcategories,
              registrationDeadline: event.registrationDeadline, total_weeks: event.total_weeks,
              matches_per_week: event.matches_per_week, maxParticipants: event.maxParticipants,
              participants: event.participants, address: event.address, postal_code: event.postal_code,
            }} onLeagueClick={() => handleEventClick(event)} />
          ) : (
            <TournamentCard key={event.id} tournament={{
              id: event.id, title: event.title, organizer: profile.name, sport: event.sport,
              date: event.date, location: event.location, registrationFee: event.registrationFee,
              imageUrl: event.imageUrl,
              matchType: event.matchType,
              genderCategories: event.genderCategories,
              subcategories: event.subcategories,
              maxParticipants: event.maxParticipants,
              participants: event.participants,
            } as any} onTournamentClick={() => handleEventClick(event)} />
          )
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen -mx-4 -mt-4">
      {/* White top section */}
      <div className="bg-white px-4 pt-4 pb-2 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="p-2 -ml-2 shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="flex-1 text-xl font-bold text-foreground line-clamp-1 leading-tight">{profile.name}</h2>
          <ChatIcon onClick={handleChatClick} />
        </div>

        {/* Avatar + Stats Row */}
        <div className="flex items-center gap-4">
          <Avatar className="h-20 w-20 shrink-0 ring-2 ring-[#e7d1b6]">
            <AvatarImage src={profile.avatar} alt={profile.name} />
            <AvatarFallback className="bg-gradient-to-br from-[#1b3521] via-[#31633f] to-[#6da079] text-[#e7d1b6] font-semibold text-lg">
              {getInitials(profile.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 flex justify-around">
            <div className="flex flex-col items-center">
              <span className="text-lg font-bold text-foreground">{profile.tournamentsCount}</span>
              <span className="text-xs text-muted-foreground">{t('organizerProfile.tournaments')}</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-lg font-bold text-foreground">{profile.leaguesCount}</span>
              <span className="text-xs text-muted-foreground">{t('organizerProfile.leagues')}</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-lg font-bold text-foreground">{localFollowersCount}</span>
              <span className="text-xs text-muted-foreground">
                {localFollowersCount === 1 ? t('organizerProfile.follower') : t('organizerProfile.followers')}
              </span>
            </div>
          </div>
        </div>

        {/* Description */}
        {profile.description && (
          <div>
            <p className={`text-sm text-muted-foreground leading-relaxed ${
              !showFullDescription && profile.description.length > 200 ? 'line-clamp-3' : ''
            }`}>
              {profile.description}
            </p>
            {profile.description.length > 200 && (
              <button
                onClick={() => setShowFullDescription(!showFullDescription)}
                className="text-primary hover:text-primary/80 text-xs font-medium mt-1 flex items-center gap-0.5"
              >
                {showFullDescription ? t('organizerProfile.showLess') : t('organizerProfile.showMore')}
                <ChevronDown className={`h-3 w-3 transition-transform ${showFullDescription ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
        )}

        {/* Website */}
        {profile.website && (
          <a
            href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-primary hover:text-primary/80 text-sm transition-colors"
          >
            <Globe className="h-4 w-4" />
            {profile.website.replace(/^https?:\/\//, '')}
          </a>
        )}

        {/* Sponsors */}
        {profile.sponsors.length > 0 && (
          <div className="py-2">
            <div className="flex items-center justify-start gap-1.5 mb-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[#8B7355]">
                {t('organizerProfile.sponsors')}
              </span>
            </div>
            <div className="flex gap-3 overflow-x-auto justify-start py-1">
              {profile.sponsors.map(sponsor => (
                <div key={sponsor.id} className="flex flex-col items-center gap-1.5 min-w-[56px]">
                  {sponsor.logoUrl ? (
                    <img src={sponsor.logoUrl} alt={sponsor.name} className="h-9 w-9 rounded-full object-cover ring-1 ring-[#d4b896] shadow-sm" />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-[#f5f0e8] flex items-center justify-center text-[10px] font-bold text-[#8B7355] ring-1 ring-[#d4b896] shadow-sm">
                      {sponsor.name.charAt(0)}
                    </div>
                  )}
                  <span className="text-[10px] text-[#8B7355]/80 text-center leading-tight">{sponsor.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          {user && (
            <Button
              className={`flex-1 ${isFollowing
                ? 'bg-[#e6e8e6]/[0.57] border border-[#e6e8e6] text-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive'
                : 'bg-[#3D6B4F] text-white hover:bg-[#3D6B4F]/90'}`}
              onClick={isFollowing ? handleUnfollow : handleFollow}
            >
              {isFollowing ? `${t('organizerProfile.following')} ✓` : t('organizerProfile.follow')}
            </Button>
          )}
        </div>
      </div>

      {/* Tabs: Torneos | Ranking | Ligas */}
      <Tabs defaultValue="tournaments" className="w-full">
        <div className="bg-white px-4 pb-2">
          <TabsList className="w-full grid grid-cols-3 bg-white">
            <TabsTrigger value="tournaments" className="text-xs border border-[#e6e8e6]/[0.57] rounded-md data-[state=active]:bg-[#3D6B4F] data-[state=active]:text-white data-[state=active]:border-[#3D6B4F]">
              <Trophy className="h-3.5 w-3.5 mr-1" />
              {t('organizerProfile.tournaments')}
            </TabsTrigger>
            <TabsTrigger value="ranking" className="text-xs border border-[#e6e8e6]/[0.57] rounded-md data-[state=active]:bg-[#3D6B4F] data-[state=active]:text-white data-[state=active]:border-[#3D6B4F]">
              {t('organizerProfile.ranking')}
            </TabsTrigger>
            <TabsTrigger value="leagues" className="text-xs border border-[#e6e8e6]/[0.57] rounded-md data-[state=active]:bg-[#3D6B4F] data-[state=active]:text-white data-[state=active]:border-[#3D6B4F]">
              <Users className="h-3.5 w-3.5 mr-1" />
              {t('organizerProfile.leagues')}
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="px-4 py-4 pb-20">
          {/* Tournaments Tab */}
          <TabsContent value="tournaments" className="space-y-4">
            {profile.upcomingTournaments.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">{t('organizerProfile.upcoming')}</h3>
                {renderEventList(profile.upcomingTournaments, 'tournaments')}
              </div>
            )}
            {profile.pastTournaments.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">{t('organizerProfile.past')}</h3>
                {renderEventList(profile.pastTournaments, 'tournaments')}
              </div>
            )}
            {profile.upcomingTournaments.length === 0 && profile.pastTournaments.length === 0 && (
              <div className="text-center py-8">
                <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">{t('organizerProfile.noUpcomingTournaments')}</p>
              </div>
            )}
          </TabsContent>

          {/* Ranking Tab */}
          <TabsContent value="ranking">
            <OrganizerRankingTab organizerId={profile.userId} />
          </TabsContent>

          {/* Leagues Tab */}
          <TabsContent value="leagues" className="space-y-4">
            {profile.upcomingLeagues.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">{t('organizerProfile.upcoming')}</h3>
                {renderEventList(profile.upcomingLeagues, 'leagues')}
              </div>
            )}
            {profile.pastLeagues.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">{t('organizerProfile.past')}</h3>
                {renderEventList(profile.pastLeagues, 'leagues')}
              </div>
            )}
            {profile.upcomingLeagues.length === 0 && profile.pastLeagues.length === 0 && (
              <div className="text-center py-8">
                <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">{t('organizerProfile.noUpcomingLeagues')}</p>
              </div>
            )}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default OrganizerProfile;
